import { DOCUMENT, isPlatformServer, NgClass } from '@angular/common';
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    PLATFORM_ID,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import { CLOUDFLARE_IMAGE_CONFIG, CLOUDINARY_IMAGE_CONFIG } from '@hyundai/ng-common-lib';
import type { HyundaiImage, ImageSize } from './m000-c010-image.interface';

@Component({
    selector: 'ui-m000-c010-image',
    standalone: true,
    imports: [NgClass],
    templateUrl: './m000-c010-image.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class M000C010ImageComponent {
    protected readonly renderer2 = inject(Renderer2);
    protected readonly document = inject(DOCUMENT);
    protected readonly platformId = inject(PLATFORM_ID);
    protected readonly cloudinaryImageConfig = inject(CLOUDINARY_IMAGE_CONFIG);
    protected readonly cloudflareImageConfig = inject(CLOUDFLARE_IMAGE_CONFIG);

    protected readonly FALLBACK_IMAGE = '_static/fallback-image';

    image = input.required<HyundaiImage>();
    alt = input.required<string>();
    priority = input.required({ transform: booleanAttribute });
    title = input<string>();

    classList = input<string>();
    errorImageUrl = input<string>();
    ignoreHeight = input(false, { transform: booleanAttribute });
    cover = input(false, { transform: booleanAttribute });
    highRes = input(false, { transform: booleanAttribute });
    focusFace = input(false, { transform: booleanAttribute });
    contain = input(false, { transform: booleanAttribute });

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    onError = output();

    images = computed(() => {
        const d = this.image();
        if (!d || (d && !d.image)) return null;

        if (!d.image.includes('http') || d.image.includes('cloudinary')) {
            return {
                desktop2xl: d.desktop2xl
                    ? this.getCloudinaryImage(d.desktop2xl?.url ?? d.image, d.desktop2xl)
                    : undefined,
                desktop: this.getCloudinaryImage(d.desktop.url ?? d.image, d.desktop),
                tablet: this.getCloudinaryImage(d.tablet.url ?? d.image, d.tablet),
                mobile: this.getCloudinaryImage(d.mobile.url ?? d.image, d.mobile),
            };
        }
        return {
            desktop2xl: d.desktop2xl ? this.getCloudflareImage(d.desktop2xl?.url ?? d.image, d.desktop2xl) : undefined,
            desktop: this.getCloudflareImage(d.desktop.url ?? d.image, d.desktop),
            tablet: this.getCloudflareImage(d.tablet.url ?? d.image, d.tablet),
            mobile: this.getCloudflareImage(d.mobile.url ?? d.image, d.mobile),
        };
    });

    preloadLinksEffect = effect(() => {
        const imagesLinks = this.images();
        if (this.priority() && this.image() && imagesLinks && isPlatformServer(this.platformId)) {
            const createPreloadLink = (href: string, media: string) => {
                const link = this.renderer2.createElement('link');
                link.setAttribute('href', href);
                link.setAttribute('media', media);
                link.setAttribute('rel', 'preload');
                link.setAttribute('as', 'image');
                this.renderer2.appendChild(this.document.head, link);
            };

            createPreloadLink(imagesLinks.mobile, '(max-width: 767px)');
            createPreloadLink(imagesLinks.tablet, '(min-width: 768px) and (max-width: 1023px)');
            if (imagesLinks.desktop2xl) {
                createPreloadLink(imagesLinks.desktop, '(min-width: 1024px) and (max-width: 1535px)');
                createPreloadLink(imagesLinks.desktop2xl, '(min-width: 1536px)');
            } else {
                createPreloadLink(imagesLinks.desktop, '(min-width: 1024px)');
            }
        }
    });

    protected getCloudinaryImage(image: string | undefined, size: ImageSize): string {
        image ??= this.FALLBACK_IMAGE;
        const isExternal = image.startsWith('http');
        const transformations = this.getCloudinaryProperties(size);
        return `${this.cloudinaryImageConfig.baseUrl}/image/${isExternal ? 'fetch' : 'upload'}/${transformations.join(',')}/q_80,f_webp/${image}`;
    }

    protected getCloudinaryProperties(size: ImageSize): string[] {
        const transformations: string[] = [`w_${size.width}`];
        if (size.height) {
            transformations.push(`h_${size.height}`);
        }

        if (this.focusFace()) {
            transformations.push('c_fill', 'g_face');
        } else if (this.cover()) {
            transformations.push('c_fill');
        } else if (this.contain()) {
            transformations.push('c_pad');
        } else if (this.ignoreHeight()) {
            transformations.push('c_crop');
        }

        if (this.highRes()) {
            transformations.push('dpr_2.0');
        }

        return transformations;
    }

    protected getCloudflareImageProperties(size: ImageSize): string[] {
        const transformations: string[] = [`width=${size.width}`];
        if (size.height) {
            transformations.push(`height=${size.height}`);
        }
        if (this.focusFace()) {
            transformations.push('fit=cover', 'gravity=face');
        } else if (this.cover()) {
            transformations.push('fit=cover');
        } else if (this.contain()) {
            transformations.push('fit=contain');
        } else if (this.ignoreHeight()) {
            transformations.push('fit=crop');
        }

        if (this.highRes()) {
            transformations.push('dpr=2');
        }

        return transformations;
    }

    protected getCloudflareImage(image: string = this.FALLBACK_IMAGE, size: ImageSize): string {
        return `${this.cloudflareImageConfig}/cdn-cgi/image/format=auto,slow-connection-quality=50,${this.getCloudflareImageProperties(size).join(',')}/${image}`;
    }
}
